import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Compass, 
  ArrowRight,
  Sparkles,
  Users,
  LogIn
} from 'lucide-react';
import { motion } from 'framer-motion';

const ExplorePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/explore" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
              <Compass className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-lg text-slate-800">Nysa</span>
          </Link>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/login')}
              className="text-slate-600"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Log in
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Sensory-friendly venue experiences
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Explore spaces<br />
              <span className="text-teal-600">before you visit</span>
            </h1>
            
            <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
              Understand how a place feels — before you go. 
              Interactive maps, virtual tours, and sensory guides to help you plan your visit with confidence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section for Venues */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 text-white/90 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Users className="w-4 h-4" />
              For venues & organisations
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Want to offer this experience<br />for your visitors?
            </h2>
            
            <p className="text-lg text-slate-300 max-w-xl mx-auto mb-8">
              Create an interactive sensory guide for your venue and help visitors plan their visit with confidence.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => navigate('/register')}
                className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8 h-14 text-lg"
              >
                Create your experience
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => navigate('/login')}
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 h-14 text-lg"
              >
                Log in
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <Compass className="w-5 h-5 text-teal-600" />
            <span>Nysa Sensory Experiences</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/terms" className="hover:text-slate-700">Terms</Link>
            <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ExplorePage;
